/**
 * Loads a doc's Markdown body.
 *
 * Each file under `content/` becomes its own lazy chunk, so the index page never downloads any
 * doc content — only the metadata that already lives in the generated manifest.
 */
const loaders = import.meta.glob("../../content/*.md", {
  query: "?raw",
  import: "default",
}) as Record<string, () => Promise<string>>;

const bySlug = new Map(
  Object.entries(loaders).map(([filePath, load]) => [
    filePath.slice(filePath.lastIndexOf("/") + 1, -".md".length),
    load,
  ]),
);

const FRONTMATTER = /^---\r?\n[\s\S]*?\r?\n---\r?\n?/;

/** Markdown body of a doc, with the frontmatter block stripped. */
export const loadDocBody = async (slug: string): Promise<string> => {
  const load = bySlug.get(slug);

  if (!load) throw new Error(`No content file for doc "${slug}"`);

  return (await load()).replace(FRONTMATTER, "");
};

/**
 * Anchor id for a heading. Used both by the in-page nav and by the rendered `<h2>`, so the two
 * always agree without a rehype pass in between.
 */
export const headingId = (title: string): string =>
  title
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");

/** `##` headings, in order — the doc's in-page navigation. Fenced code is skipped. */
export const collectSections = (body: string): { id: string; title: string }[] =>
  [...body.replace(/^```[\s\S]*?^```/gm, "").matchAll(/^##\s+(.+)$/gm)].map((match) => {
    const title = match[1].trim();

    return { id: headingId(title), title };
  });
